/**
 * Poll bulk-reserved-import job after 202 Accepted so the user can close the modal.
 * Invokes onCompleted when the job finishes successfully (for list refresh).
 */

import { getBulkReservedImportStatus } from '../../utilities/api'
import { mergeBulkImportResults } from './bulkReservedImportShared'

const DEFAULT_POLL_MS = 2500
const DEFAULT_MAX_MS = 2 * 60 * 60 * 1000

/**
 * @param {string} batchId
 * @param {{ onCompleted?: (job: object) => void, onFailed?: (err: Error) => void, skippedRows?: object[], pollMs?: number, maxWaitMs?: number }} [options]
 */
export function startBulkReservedImportPoll(batchId, options = {}) {
  const {
    onCompleted,
    onFailed,
    skippedRows = [],
    pollMs = DEFAULT_POLL_MS,
    maxWaitMs = DEFAULT_MAX_MS,
  } = options
  const id = String(batchId ?? '').trim()
  if (!id) return

  const started = Date.now()

  const dispatch = (detail) => {
    window.dispatchEvent(
      new CustomEvent('bulk-reserved-import-complete', { detail })
    )
  }

  const poll = async () => {
    if (Date.now() - started > maxWaitMs) {
      dispatch({ batchId: id, timeout: true, results: null })
      onFailed?.(new Error('Timed out waiting for import'))
      return
    }
    try {
      const res = await getBulkReservedImportStatus(id)
      const job = res?.data
      const status = job?.status
      if (status === 'completed') {
        const mergedResults = mergeBulkImportResults(job?.results, skippedRows)
        const jobWithSkipped = { ...job, results: mergedResults }
        dispatch({ batchId: id, job: jobWithSkipped, failed: false })
        onCompleted?.(jobWithSkipped)
        return
      }
      if (status === 'failed') {
        const errMsg = job?.error || 'Import failed'
        dispatch({ batchId: id, job, failed: true, error: errMsg })
        onFailed?.(new Error(errMsg))
        return
      }
      setTimeout(poll, pollMs)
    } catch (e) {
      setTimeout(poll, pollMs)
    }
  }

  setTimeout(poll, pollMs)
}
